import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { RegistrationStore } from "@app/pages/registration/stores/RegistrationStore";
import { OauthService } from "@app/services/oauth.service";
import { Subject, take } from "rxjs";
import { WindowToken } from "@app/util/window.token";
import { LogoutService } from "@app/services/Logout.service";
import { AppDataService } from "@app/services/appData.service";
import { ActivatedRoute, Router } from "@angular/router";
import { SpinnerService } from "@app/services/spinner.service";
import {
    AuthTokenData,
    AuthTokenService,
} from "@app/services/auth-token.service";
import { ErrorDialogService } from "@app/services/error-dialog.service";
import { NavigationService } from "@app/services/navigation.service";
import { finalize, takeUntil } from "rxjs/operators";
import { SsoHomeownerApiService } from "@app/services/sso/sso-homeowner-api.service";

@Component({
    selector: "app-callback",
    template: "",
})
export class CallbackComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    constructor(
        @Inject(OauthService) private oauthService: OauthService,
        @Inject(WindowToken) private window,
        @Inject(LogoutService) private logoutService: LogoutService,
        @Inject(AppDataService) private appDataService: AppDataService,
        @Inject(ActivatedRoute) private route: ActivatedRoute,
        @Inject(SpinnerService) private spinnerService: SpinnerService,
        @Inject(AuthTokenService) private authTokenService: AuthTokenService,
        @Inject(RegistrationStore) private registrationStore: RegistrationStore,
        @Inject(NavigationService) private navigationService: NavigationService,
        @Inject(ErrorDialogService) private errorDialogService: ErrorDialogService,
        @Inject(SsoHomeownerApiService) private ssoHomeownerApiService: SsoHomeownerApiService,
        @Inject(Router) private router: Router
    ) {}

    ngOnInit(): void {
        this.spinnerService.showSpinner();

        this.route.fragment
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe(fragment => {
                if (fragment) {
                    this.processUrlFragment(fragment);
                } else {
                    this.handleAccessDenied("No token fragment found in URL.");
                }
            });
    }

    processUrlFragment(fragment: string) {
        const params = new URLSearchParams(fragment);
        const accessToken = params.get("access_token");

        if (accessToken) {
            const tokenData: AuthTokenData = {
                access_token: accessToken,
                id_token: params.get("id_token"),
                expires_in: params.get("expires_in"),
                account_username: params.get("account_username"),
            };

            this.authTokenService.storeTokens(tokenData);
            this.validateUser();
        } else {
            this.handleAccessDenied("No access token found in URL.");
        }
    }

    validateUser() {
        this.oauthService.getUser()
            .pipe(finalize (() => this.spinnerService.closeSpinner()),
                takeUntil(this.destroy$)
            )
            .subscribe({
                next: (user) => this.handleUserValidationSuccess(user),
                error: (err) => this.handleUserValidationError(err)
            });
    }

    handleUserValidationSuccess(user: any) {
        if (user.homeowner) {
            this.navigationService.navigateToAppAfterLogin();
        } else {
            this.handleUserNotLinked();
        }
    }

    handleUserNotLinked() {
        if (this.authTokenService.isSsoAttempt()) {
            this.navigationService.navigateToSso();
        } else {
            this.handleUnableToGetToken(
                "User was logged in successfully but failed to retrieve the homeowner token."
            );
        }
    }

    handleUserValidationError(error: any) {
        console.error("Failed to retrieve user after token exchange: , error");
        this.handleUnableToGetToken(
            "Failed to retrieve user details after login."
        );
    }

    handleUnableToGetToken(reason: string) {
        console.error("handleUnableToGetToken:", reason);

        this.errorDialogService
            .showError(
                "Access Denied",
                "User was logged in successfully but failed to retrieve the homeowner token. Please contact customer support to report this issue."
            )
            .pipe(take(1))
            .subscribe(() => {
                this.registrationStore.clearAllData();

                const idToken = this.authTokenService.getIdToken();
                this.authTokenService.clearAllSessionData();

                if (idToken) {
                    this.logoutService.logout();
                } else {
                    this.navigationService.navigateToHome();
                }
            });
    }

    private handleAccessDenied(reason: string): void {
        this.spinnerService.closeSpinner();
        console.error("handleAccessDenied:", reason);

        if (this.authTokenService.isSsoAttempt()) {
            this.navigationService.navigateToSso();
            return;
        }

        this.authTokenService.clearLoginAttemptFlags();
        this.errorDialogService
            .showError(
                "Access Denied",
                "User is not assigned to the client application. Please Sign Up to register for Property Pay Access."
            )
            .pipe(take(1))
            .subscribe(() => {
                this.navigationService.navigateToHome();
            });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
